import React, { useCallback } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { TopicCardRecord } from './types';

import { Link } from 'react-router-dom';
import ElementCard from 'src/components/BeaconV2Components/ElementCard/ElementCard';
import TopicOwner from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/TopicOwner';
import {
  SlCardContainer,
  SlCardHeader,
  SlCardTitle,
  SlCardSplineChart,
  SlCardFooter,
  SlCardBack
} from 'src/components/BeaconV2Components/SlCard';
import { SlChangeAnnotation } from 'src/components/BeaconV2Components/SlChangeAnnotation/SlChangeAnnotation';
import { SlFlippableCard } from 'src/components/BeaconV2Components/SlFlippableCard';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useAppSelector, useHistory, useMetricFormatter } from 'src/utils/Hooks';
import _capitalize from 'lodash/capitalize';
import { TOPIC_GROUPS_PER_CARD } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/TopicGroupSettingsPage';
import MiniButton from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/MiniButton';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import { useDeleteTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useDeleteTopicModal';
import {
  StorageSource,
  useTopicBuilderActions
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { useDeleteTopicGroupModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useDeleteTopicGroupModal';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';

interface TopicCardProps {
  topic: TopicCardRecord;
}

/**
 * Used to render traffic data for a topic or topic
 * group on the Topics page
 */
export function TopicCard({ topic }: TopicCardProps) {
  const formatMetric = useMetricFormatter();
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const theme = useStacklineTheme();
  const { openModal: openTopicModal } = useDeleteTopicModal();
  const { openModal: openTopicGroupDeletionModal } = useDeleteTopicGroupModal();
  const { openModal: openAddTopicGroupModal, setTopicGroupState } = useAddTopicGroupStoreActions();
  const history = useHistory();

  const { storage } = useTopicBuilderActions();

  const miniButtonStyles = {
    backgroundColor:
      topic.type === 'topic' ? hexToRgba(theme.colors.accentTangerine, 0.1) : hexToRgba(theme.colors.accentPlum, 0.1)
  };
  const sx = {
    ...miniButtonStyles,
    '&:hover': {
      ...miniButtonStyles
    }
  };

  const buttonColor = topic.type === 'topic' ? 'accentTangerine' : 'accentPlum';

  /**
   * Handles the click event for deleting a topic
   */
  const handleDelete = useCallback(() => {
    openTopicModal({ topicId: topic.id, topicName: topic.name });
  }, [openTopicModal, topic]);

  /**
   * Handles the click event for deleting a topic group
   */
  const handleGroupDelete = useCallback(() => {
    openTopicGroupDeletionModal({ topicGroupId: topic.id, topicGroupName: topic.name });
  }, [openTopicGroupDeletionModal, topic]);

  /**
   * Handles the click event for duplicating a topic
   */
  const handleDuplicateClick = useCallback(() => {
    const { topicMetadata } = topic;
    // Save the topic to local storage so that it can be retrieved when the user navigates to the topic builder
    storage.save(topicMetadata, StorageSource.DUPLICATE);

    history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
  }, [history, searchParams, topic, storage]);

  /**
   * Handles the click event for duplicating a topic group
   */
  const handleGroupDuplicateClick = useCallback(() => {
    setTopicGroupState(({ setCategoryIds }) => setCategoryIds(topic?.topicMetadata?.includedCategoryIds ?? []));
    setTopicGroupState(({ setSubcategoryIds }) =>
      setSubcategoryIds(topic?.topicMetadata?.includedSubCategoryIds ?? [])
    );
    setTopicGroupState(({ setTopicIds }) => setTopicIds(topic?.topicMetadata?.topicIds ?? []));
    openAddTopicGroupModal();
  }, [openAddTopicGroupModal, setTopicGroupState, topic]);

  /**
   * Handles the click event for editing a topic
   */
  const handleEditClick = useCallback(() => {
    // No need to save the topic to local storage since the TopicSettingsPage will pull the topic from the entity
    history.push(`/topic/${topic.id}${searchParams}&tab=traffic&subtab=settings`);
  }, [history, searchParams, topic]);

  /**
   * Handles the click event for editing a topic group
   */
  const handleEditGroupClick = useCallback(() => {
    history.push(`/topicgroup/${topic.id}${searchParams}&tab=traffic&subtab=settings`);
  }, [history, searchParams, topic]);

  const renderButtons = useCallback(() => {
    switch (topic?.type) {
      case 'topic':
        switch (topic.ownership) {
          case 'Owned':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleEditClick} sx={sx}>
                  <Text color={buttonColor}>Edit</Text>
                </MiniButton>
                <MiniButton onClick={handleDelete} sx={sx}>
                  <Text color={buttonColor}>Delete</Text>
                </MiniButton>
                <MiniButton onClick={handleDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Custom':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Standard':
            return null;

          default:
            return null;
        }

      case 'topicGroup':
        switch (topic.ownership) {
          case 'Owned':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleEditGroupClick} sx={sx}>
                  <Text color={buttonColor}>Edit</Text>
                </MiniButton>
                <MiniButton onClick={handleGroupDelete} sx={sx}>
                  <Text color={buttonColor}>Delete</Text>
                </MiniButton>
                <MiniButton onClick={handleGroupDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Custom':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleGroupDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          default:
            return null;
        }
      default:
        return null;
    }
  }, [
    buttonColor,
    sx,
    handleDelete,
    handleGroupDelete,
    topic,
    handleDuplicateClick,
    handleEditClick,
    handleEditGroupClick,
    handleGroupDuplicateClick
  ]);

  return (
    <SlFlippableCard
      sx={{
        width: '100%',
        height: '484px'
      }}
      key={topic.id}
      cardFront={() => (
        <SlCardContainer>
          <SlCardHeader>
            <ElementCard
              textSx={{ fontSize: '20px', fontWeight: 700 }}
              variant="small"
              color={topic.type === 'topic' ? 'accentTangerine' : 'accentPlum'}
            >
              {_capitalize(topic.name.slice(0, 2))}
            </ElementCard>
            <SlCardTitle sx={{ fontSize: '18px', fontWeight: 500, lineHeight: 1.4, letterSpacing: 0.08 }}>
              <Link to={`/${topic.type.toLowerCase()}/${topic.id}${searchParams}${additionalParams}`}>
                {topic.name}
              </Link>
            </SlCardTitle>
          </SlCardHeader>
          <SlCardSplineChart
            metricType={METRICTYPE.VOLUME}
            primaryData={topic.splineData}
            secondaryData={topic.comparisonSplineData}
            width={304}
            height={115}
          />
          <SlCardFooter>
            <Flex flexDirection="column" gap="sm">
              <Text variant="subtitle2" transform="uppercase">
                Total Traffic
              </Text>
              <Flex gap="sm" alignItems="center">
                <Text variant="body1">
                  {formatMetric(topic.totalTraffic, METRICTYPE.VOLUME, {
                    decimalPlaces: 2,
                    showFullValue: false
                  })}
                </Text>
                <SlChangeAnnotation
                  textVariant="subtitle2"
                  value={topic.totalTrafficPercentChange}
                  formattedValue={`${formatMetric(Math.abs(topic.totalTrafficPercentChange), METRICTYPE.PERCENT, {
                    decimalPlaces: 2
                  })}`}
                />
              </Flex>
            </Flex>
          </SlCardFooter>
        </SlCardContainer>
      )}
      cardBack={() => (
        <SlCardBack>
          <Flex flexDirection="column" gap={24}>
            {renderButtons()}
            <Flex flexDirection="column" gap="sm">
              <Text variant="subtitle1" transform="uppercase">
                {topic.type === 'topic' ? 'Topic Type' : 'Topic Group Type'}
              </Text>
              <Text variant="body2">{topic?.ownership}</Text>
            </Flex>
            {topic.type === 'topic' && (
              <Flex flexDirection="column" gap="sm">
                <Text variant="subtitle1" transform="uppercase">
                  Topic Group Inclusions
                </Text>
                {(topic?.topicGroups ?? []).map((topicGroup) => (
                  <Text
                    title={topicGroup.topicGroupName}
                    key={topicGroup.topicGroupId}
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      display: 'block',
                      overflow: 'hidden'
                    }}
                    variant="body2"
                  >
                    {topicGroup.topicGroupName}
                  </Text>
                ))}
                {(topic?.topicGroups ?? []).length >= TOPIC_GROUPS_PER_CARD && (
                  <Link to={`/topic/${topic.id}${searchParams}${additionalParams}&anchor=topic-group-inclusions`}>
                    <Text
                      underlined
                      sx={{
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      Show more
                    </Text>
                  </Link>
                )}
              </Flex>
            )}
          </Flex>

          <SlCardFooter sx={{ padding: 0 }}>
            <TopicOwner
              createdAt={topic.topicMetadata.createdAt}
              firstName={topic?.owner?.firstName}
              lastName={topic?.owner?.lastName}
              userId={topic.topicMetadata.ownedByUserId}
            />
          </SlCardFooter>
        </SlCardBack>
      )}
    />
  );
}
